import { useIonAlert } from '@ionic/react';
import { useCallback, useEffect, useMemo } from 'react';
import { useMachineConnected } from '../../../../api/hooks/machine.hooks';
import { MachineStateEnum } from '../../../../components/machine/machine-state';
import { ApplicationConfig } from '../../../../config/config';
import { getLocalizedText } from '../../../../i18n/i18n.utils';
import { useMachineContext } from '../../../../pages/machine/machine-context-provider';

export interface RobotTeachError {
  type: 'offline' | 'not_running';
  message: string;
}

export interface CanRobotTeachReturnObject {
  error?: RobotTeachError;
  present: () => void;
  dismiss: () => void;
}

const useCanRobotTeach = (): RobotTeachError | undefined => {
  const { machine } = useMachineContext();
  const isConnected = useMachineConnected({ machine });

  const isRobotRunning = useMemo(() => {
    return (
      machine?.state?.transient?.status?.machineState ===
      MachineStateEnum.ROBOT_MODE_RUNNING
    );
  }, [machine]);

  const error = useMemo<RobotTeachError | undefined>(() => {
    if (ApplicationConfig.env !== 'local') {
      if (!isConnected) {
        return {
          type: 'offline',
          message:
            getLocalizedText('common:robot_offline_alert') ||
            'Please ensure the robot is online before teaching',
        };
      } else if (!isRobotRunning) {
        return {
          type: 'not_running',
          message:
            getLocalizedText('common:robot_not_initialized_alert') ||
            'Please initialize the robot before teaching',
        };
      }
    }
  }, [isConnected, isRobotRunning]);

  return error;
};

const useCanRobotTeachWithAlertAsync = (): CanRobotTeachReturnObject => {
  const error = useCanRobotTeach();

  const [presentInternal, dismiss] = useIonAlert();

  const present = useCallback(() => {
    if (error) {
      presentInternal(error?.message);
    }
  }, [presentInternal, error]);

  return { error, present, dismiss };
};

const useCanRobotTeachWithAlert = (): RobotTeachError | undefined => {
  const { error, present } = useCanRobotTeachWithAlertAsync();

  useEffect(() => {
    if (error) {
      present();
    }

    // Note: We are ignoring since the present and dismiss functions are not wrapped in useCallback by Ionic
    //   which causes the useEffect to rerender multiple times
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return error;
};

export {
  useCanRobotTeach,
  useCanRobotTeachWithAlert,
  useCanRobotTeachWithAlertAsync,
};
